import { useState, useRef, useEffect } from 'react';
import { SUPPORTED_LANGUAGES, type LanguageCode } from '../../i18n';
import { useLanguage } from '../../hooks/useLanguage';

interface LanguageSelectorProps {
  variant?: 'compact' | 'full';
  className?: string;
  buttonClassName?: string;
}

export function LanguageSelector({ variant = 'compact', className = '', buttonClassName }: LanguageSelectorProps) {
  const { currentLanguage, changeLanguage } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = SUPPORTED_LANGUAGES.find(l => l.code === currentLanguage) || SUPPORTED_LANGUAGES[0];

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (code: LanguageCode) => {
    changeLanguage(code);
    setOpen(false);
  };

  if (variant === 'compact') {
    return (
      <div ref={ref} className={`relative ${className}`}>
        <button
          onClick={() => setOpen(!open)}
          className={`flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-white/10 transition-colors text-xs font-bold ${buttonClassName || 'text-white/80 hover:text-white'}`}
          aria-label="Change language"
        >
          {current.shortLabel}
        </button>
        {open && (
          <div className="absolute right-0 top-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg py-1 z-50 min-w-[180px]">
            {SUPPORTED_LANGUAGES.map(lang => (
              <button
                key={lang.code}
                onClick={() => handleSelect(lang.code)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                  lang.code === currentLanguage
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 font-semibold'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <span className="w-7 text-center font-bold text-xs opacity-60">{lang.shortLabel}</span>
                <span>{lang.nativeName}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Full variant — for settings/signup
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {current.code === 'en' ? 'Language' : current.nativeName}
      </label>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {SUPPORTED_LANGUAGES.map(lang => (
          <button
            key={lang.code}
            onClick={() => handleSelect(lang.code)}
            className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm transition-colors ${
              lang.code === currentLanguage
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 font-semibold'
                : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <span className="w-6 text-center font-bold text-xs opacity-60">{lang.shortLabel}</span>
            <span className="truncate">{lang.nativeName}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
